import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";
import { BLOG_POSTS } from "@/data/blog-posts";

export default function Blog() {
  return (
    <>
      <SEO
        title="Blog — Ley 21.719 y Protección de Datos en Chile"
        description="Artículos, guías y análisis sobre la Ley N° 21.719 de Protección de Datos Personales en Chile. Todo lo que tu PYME necesita saber para cumplir antes del 1 de diciembre de 2026."
        path="/blog"
        keywords="Ley 21.719, protección de datos Chile, blog protección datos, GDPR Chile, APDP, PYME cumplimiento"
        ogImage="https://leydedatoschile.com/og/og-blog.jpg"
        schemas={[
          orgSchema,
          breadcrumbSchema([
            { name: "Inicio", path: "/" },
            { name: "Blog", path: "/blog" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Blog Ley de Datos Chile",
            description:
              "Blog sobre la Ley 21.719 de Protección de Datos Personales de Chile.",
            blogPost: BLOG_POSTS.map((p) => ({
              "@type": "BlogPosting",
              headline: p.title,
              description: p.description,
              datePublished: p.date,
              author: { "@type": "Organization", name: "Ley de Datos Chile" },
              url: `https://leydedatoschile.com/blog/${p.slug}`,
            })),
          },
        ]}
      />
      <section className="bg-ink-950 text-white relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 lg:pt-24 lg:pb-20">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-6">
            <span className="inline-block w-6 h-px bg-blue-400/60" />
            Blog · Ley 21.719
          </div>
          <h1
            data-testid="blog-title"
            className="font-display text-5xl sm:text-6xl text-white leading-[1.02] tracking-tight max-w-4xl"
          >
            Guías, análisis y <em className="italic text-blue-300">respuestas claras</em>.
          </h1>
          <p className="mt-7 text-lg text-slate-300 leading-relaxed max-w-2xl">
            Todo lo que tu PYME chilena necesita saber sobre la nueva Ley de Protección de Datos Personales, en lenguaje simple y aplicable.
          </p>
          <p className="mt-8 font-mono text-[10px] tracking-[0.22em] text-slate-500 uppercase">
            {BLOG_POSTS.length} artículos · actualizados regularmente
          </p>
        </div>
      </section>

      <section className="px-6 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BLOG_POSTS.map((p, idx) => (
              <Link
                key={p.slug}
                data-testid={`post-card-${p.slug}`}
                to={`/blog/${p.slug}`}
                className="group block bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-slate-900 hover:shadow-lg transition-all"
              >
                {p.cover && (
                  <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                    <img
                      src={p.cover}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-4">
                    <span>{String(idx + 1).padStart(2, "0")}</span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      {p.readingMinutes} min
                    </span>
                  </div>
                  <h2 className="font-display text-xl font-semibold text-slate-900 leading-snug group-hover:text-blue-700 transition-colors line-clamp-3">
                    {p.title}
                  </h2>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">
                    {p.excerpt}
                  </p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-xs text-slate-500 inline-flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" />
                      {new Date(p.date).toLocaleDateString("es-CL", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700">
                      Leer
                      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
